/**
 * The overall score — the 11 model, Shayna's revision of Performance Metrics_v5.
 *
 * Points are absolute, awarded against the peer AVERAGE — not a percentile. An earlier
 * version averaged percentiles across six metrics, which forced somebody to the bottom
 * however well the whole team did. That was replaced wholesale; see DECISION-LOG.md §3.
 *
 * What changed from the v5 model (−3..+9, integers only):
 *
 *   - Facilities handled dropped from +2 to +0.5. It was worth as much as claim count
 *     while being the line most exposed to the UNKNOWN-facility question.
 *   - Collection rate (+2) and 0% touch share (+1) and open claims worked in month
 *     (+1) were added. 0% touch share was then cut to +0.5 on 2026-09-23.
 *   - Closed claim % is now called "claims billed vs resolved %" — same measure, and
 *     still the only line that can also take points AWAY.
 *   - Totals are fractional, so the old "there is no +8" invariant is gone: with a
 *     0.5 line every half-point between −3 and 11 is reachable.
 *
 * Range is −3 to +11. A rule the person has no figure for scores 0 rather than
 * being skipped — dropping it would let somebody with three missing measures
 * out-score a peer who has all seven, which is the opposite of what a missing
 * measure means.
 *
 * The peer group is RCM department group + job title. Cost per claim ALSO splits on
 * shore, because an on-shore biller compared against off-shore rates is being scored
 * on where they live.
 *
 * BELOW MINIMUM BILLING VOLUME (review #695 as Shayna rewrote it 2026-10-18;
 * measures/minimumVolume.ts): billing under 90% of the average of the bottom quarter
 * of the job title scores 0. The score is still worked out and shown beside the 0
 * (`lowVolume.computed`), and the 0 is a real 0 in reporting and rankings. Those scored
 * 0 are left out of everyone else's peer averages, as terminated staff are
 * (DECISION-LOG.md §8). `derive` works out the minimum and the peers; `scorecard`
 * takes both.
 */
import type { EmployeeRow } from '@/data/types'
import { COLLECTION_LABEL } from './collection'
import { MINIMUM_VOLUME_LABEL } from './minimumVolume'

/** Everything the scorecard needs about one person, pre-computed. */
export interface PersonStats {
  employee: string
  department: string
  jobTitle: string
  shore: 'On' | 'Off' | null
  claims: number
  costPerClaim: number | null
  /** Resolved ÷ billed. UNCAPPED — backlog clearers exceed 100%. */
  closedClaimPct: number | null
  facilities: number
  collectedPct: number | null
  zeroTouchShare: number | null
  /** Open claims touched in the period ÷ open pile. */
  openWorkedPct: number | null
}

/** One scored line. `pts` is what it is worth; `earned` is what this person got. */
export interface ScoreLine {
  key: ScoreKey
  label: string
  /** Whether beating the average means being BELOW it. */
  lowerIsBetter: boolean
  pts: number
  earned: number
  value: number | null
  peerAvg: number | null
  /** No figure for this person, so the line scores 0 and says why. */
  missing: boolean
}

export type ScoreKey =
  | 'costPerClaim'
  | 'claims'
  | 'collectedPct'
  | 'closedClaimPct'
  | 'zeroTouchShare'
  | 'openWorkedPct'
  | 'facilities'

interface Rule {
  key: ScoreKey
  label: string
  pts: number
  lowerIsBetter: boolean
  get: (p: PersonStats) => number | null
  /** Cost per claim compares inside the shore split; everything else does not. */
  shoreSplit?: boolean
  /** A zero here is "did not do it", not a value to compare. */
  zeroIsMissing?: boolean
}

export const SCORE_RULES: Rule[] = [
  {
    key: 'costPerClaim',
    label: 'Cost per claim',
    pts: 3,
    lowerIsBetter: true,
    get: (p) => p.costPerClaim,
    shoreSplit: true,
  },
  {
    key: 'claims',
    label: 'Claim count',
    pts: 2,
    lowerIsBetter: false,
    get: (p) => p.claims,
    zeroIsMissing: true,
  },
  {
    key: 'collectedPct',
    label: COLLECTION_LABEL,
    pts: 2,
    lowerIsBetter: false,
    get: (p) => p.collectedPct,
  },
  {
    key: 'closedClaimPct',
    label: 'Claims billed vs resolved %',
    pts: 2,
    lowerIsBetter: false,
    get: (p) => p.closedClaimPct,
  },
  {
    key: 'zeroTouchShare',
    label: '0% touch share',
    // +1 in the 2026-09-16 revision; Shayna cut it to +0.5 on 2026-09-23.
    pts: 0.5,
    lowerIsBetter: false,
    get: (p) => p.zeroTouchShare,
  },
  {
    key: 'openWorkedPct',
    label: 'Open claims worked in month %',
    pts: 1,
    lowerIsBetter: false,
    get: (p) => p.openWorkedPct,
  },
  {
    key: 'facilities',
    label: 'Facilities handled',
    pts: 0.5,
    lowerIsBetter: false,
    get: (p) => p.facilities,
    zeroIsMissing: true,
  },
]

/** The one line that can take points away, and it is absolute, not vs peers. */
export const PENALTY = {
  key: 'closedPenalty' as const,
  label: 'Claims billed vs resolved % under 50%',
  threshold: 0.5,
  pts: -3,
}

export const SCORE_MAX = SCORE_RULES.reduce((s, r) => s + r.pts, 0) // 11
export const SCORE_MIN = PENALTY.pts // −3

/** Claim floor to enter a peer average. */
export const CLAIM_FLOOR = 1

export interface Scorecard {
  lines: ScoreLine[]
  /** The penalty line, always present so the reader can see it did not fire. */
  penalty: { applied: boolean; pts: number; value: number | null }
  total: number
  /** Peer group size, and the shore-split size cost per claim actually used. */
  peerN: number
  shoreN: number
  /** False when the person has no claims, or has left: they are not scored at all. */
  scored: boolean
  /**
   * The person has left, so is not scored even with claims (DECISION-LOG.md §8). Set
   * by `derive`, which also keeps them out of everybody's peer averages.
   */
  terminated?: boolean
  /**
   * Below Minimum Billing Volume, so `total` is 0 (review #695). `computed` is what the
   * lines and penalty would have made it; `claims`, `bar` and `avg` are the test, for
   * the reader. Absent when the override did not fire.
   */
  lowVolume?: LowVolume
}

export interface LowVolume {
  computed: number
  /** Billed claims over the months tested. */
  claims: number
  /** The minimum: 90% of the bottom quarter's average (summed over the months tested). */
  bar: number
  /** The bottom quarter's average billed claims (summed likewise). */
  avg: number
}

/**
 * The override is on (review #695 as rewritten). An object, not a constant, so tests
 * and a quick switch-off (as #241 did for the first version) need no other change.
 */
export const LOW_VOLUME = { enabled: true }

/** True when `claims` is under the minimum `bar`. No minimum, no test. */
export const isLowVolume = (claims: number, bar: number | null): bar is number =>
  LOW_VOLUME.enabled && bar !== null && Number.isFinite(bar) && bar > 0 && claims < bar

const n0 = (v: number) => Math.round(v).toLocaleString('en-US')

/** What a score overridden for volume says on hover. */
export const lowVolumeReason = (lv: LowVolume) =>
  `${MINIMUM_VOLUME_LABEL}: ${n0(lv.claims)} billed claims, under the minimum of ${n0(lv.bar)} ` +
  `(90% of ${n0(lv.avg)}, the average of the bottom 25% of the job title). ` +
  `Scored 0; the score before the override was ${formatPoints(lv.computed)}.`

/** What an unscored badge says on hover. */
export const unscoredReason = (sc: Pick<Scorecard, 'terminated'>) =>
  sc.terminated
    ? 'Terminated — not scored. Peer averages use active staff only.'
    : 'No claims attributed, so no score.'

const eligible = (p: PersonStats) => p.claims >= CLAIM_FLOOR

const mean = (xs: number[]): number | null =>
  xs.length === 0 ? null : xs.reduce((a, b) => a + b, 0) / xs.length

/** Peer group for most lines: same RCM department + same job title. */
const deptTitlePeers = (all: readonly PersonStats[], p: PersonStats) =>
  all.filter(
    (o) => o.department === p.department && o.jobTitle === p.jobTitle && eligible(o),
  )

/** Cost per claim additionally splits on shore — on-shore compared against on-shore. */
const deptTitleShorePeers = (all: readonly PersonStats[], p: PersonStats) =>
  deptTitlePeers(all, p).filter((o) => o.shore === p.shore)

/** The peer average for one rule, over peers who have a figure for it. */
export function peerAverage(
  all: readonly PersonStats[],
  p: PersonStats,
  rule: Rule,
): number | null {
  const peers = rule.shoreSplit ? deptTitleShorePeers(all, p) : deptTitlePeers(all, p)
  return mean(peers.map(rule.get).filter((v): v is number => v !== null))
}

/**
 * One person's scorecard against `all` (the peers whose figures make the averages).
 * `volume`, when given, is the minimum billing volume test over the months scored
 * (minimumVolume.ts `rangeVolume`): billed claims under `bar` make the total 0.
 */
export function scorecard(
  all: readonly PersonStats[],
  p: PersonStats,
  volume: { claims: number; bar: number; avg: number } | null = null,
): Scorecard {
  const peers = deptTitlePeers(all, p)
  const shorePeers = deptTitleShorePeers(all, p)

  const lines: ScoreLine[] = SCORE_RULES.map((rule) => {
    const value = rule.get(p)
    const peerAvg = peerAverage(all, p, rule)
    const missing =
      value === null ||
      !Number.isFinite(value) ||
      (rule.zeroIsMissing === true && value === 0) ||
      peerAvg === null

    const beats =
      !missing && (rule.lowerIsBetter ? value! < peerAvg! : value! > peerAvg!)

    return {
      key: rule.key,
      label: rule.label,
      lowerIsBetter: rule.lowerIsBetter,
      pts: rule.pts,
      earned: beats ? rule.pts : 0,
      value,
      peerAvg,
      missing,
    }
  })

  const applied =
    p.closedClaimPct !== null &&
    Number.isFinite(p.closedClaimPct) &&
    p.closedClaimPct < PENALTY.threshold

  const computed =
    lines.reduce((s, l) => s + l.earned, 0) + (applied ? PENALTY.pts : 0)
  const low = p.claims > 0 && volume !== null && isLowVolume(volume.claims, volume.bar)

  return {
    lines,
    penalty: { applied, pts: PENALTY.pts, value: p.closedClaimPct },
    total: low ? 0 : computed,
    peerN: peers.length,
    shoreN: shorePeers.length,
    scored: p.claims > 0,
    ...(low ? { lowVolume: { computed, ...volume! } } : {}),
  }
}

/** Fractional points print with one decimal; whole ones do not. */
export const formatPoints = (v: number): string =>
  (v > 0 ? '+' : '') + (Number.isInteger(v) ? String(v) : v.toFixed(1))

/**
 * Colour band for a total, on the same thirds the rest of the app uses.
 * Anything negative is its own band — a penalty is not merely a low score.
 */
export function scoreBand(total: number): 'good' | 'mid' | 'low' | 'bad' {
  if (total < 0) return 'bad'
  if (total >= SCORE_MAX * 0.65) return 'good'
  if (total >= SCORE_MAX * 0.3) return 'mid'
  return 'low'
}

/** Convenience: build PersonStats for every person from folded employee rows. */
export function toPersonStats(
  people: readonly EmployeeRow[],
  closedPct: (employee: string) => number | null,
  facilities: (employee: string) => number,
  extra: (employee: string) => {
    zeroTouchShare: number | null
    openWorkedPct: number | null
  },
): PersonStats[] {
  return people.map((r) => ({
    employee: r.employee,
    department: r.department,
    jobTitle: r.jobTitle,
    shore: r.shore,
    claims: r.claims,
    costPerClaim: r.claims === 0 ? null : r.claimLabor / r.claims,
    closedClaimPct: closedPct(r.employee),
    facilities: facilities(r.employee),
    collectedPct: r.collectedPct,
    ...extra(r.employee),
  }))
}

/**
 * What one person's distance from their PEER AVERAGE costs, across their claims.
 *
 * Deliberately on the scorecard's own peer group — department + job title, split by
 * shore — rather than on the job-title median the Employees table used to use. A
 * table that shows a score computed one way and a dollar gap computed another is
 * asking the reader to decide which peer group is the real one.
 */
export function costAbovePeerAverage(
  all: readonly PersonStats[],
  p: PersonStats,
): number | null {
  if (p.costPerClaim === null || p.claims <= 0) return null
  const rule = SCORE_RULES.find((r) => r.key === 'costPerClaim')!
  const avg = peerAverage(all, p, rule)
  if (avg === null || !Number.isFinite(avg)) return null
  return (p.costPerClaim - avg) * p.claims
}

/* ------------------------------------------------------------------ the gaps */

/** One point this person is not earning, and what closing it would take. */
export interface ScoreGap {
  key: ScoreKey | 'penalty' | 'lowVolume'
  /** What the line is worth — 3 for clearing the penalty, since it is worth −3. */
  pts: number
  label: string
  /** The concrete action, in the units of the measure. */
  need: string
  /** Where they stand against the bar, for the reader who wants to check. */
  detail: string
}

const GAP_TEXT: Record<ScoreKey, (v: number, avg: number) => string> = {
  costPerClaim: (v, avg) =>
    `Bring cost per claim under ${money2(avg)} — ${money2(v - avg)} a claim to find`,
  claims: (v, avg) => `Bill ${int(Math.ceil(avg - v))} more claims over the period`,
  collectedPct: (v, avg) => `Lift collection rate by ${pts(avg - v)}`,
  closedClaimPct: (v, avg) => `Close ${pts(avg - v)} more of what is billed`,
  zeroTouchShare: (v, avg) => `Lift 0% touch share by ${pts(avg - v)}`,
  openWorkedPct: (v, avg) => `Work ${pts(avg - v)} more of the open pile in the month`,
  facilities: (v, avg) =>
    `Work ${int(Math.max(1, Math.floor(avg) - v + 1))} more ${
      Math.max(1, Math.floor(avg) - v + 1) === 1 ? 'facility' : 'facilities'
    }`,
}

const money2 = (v: number) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const int = (v: number) => Math.round(v).toLocaleString('en-US')
const pts = (v: number) => `${(v * 100).toFixed(1)} points`
const pctOf = (v: number) => `${(v * 100).toFixed(1)}%`

/**
 * Every point this person is NOT earning, dearest first.
 *
 * The scorecard says where somebody stands; this says what to do about it, which is
 * the only form a score can take that is useful in a one-to-one. A line with no
 * figure is deliberately absent: "get a collection rate" is not an action a biller
 * can take, and listing it as one would make the pipeline's gap look like theirs.
 *
 * Clearing the penalty is listed as worth 3 rather than −3 — it is the single largest
 * move available to anyone carrying it, and it should sort to the top.
 */
export function scoreGaps(sc: Scorecard): ScoreGap[] {
  const out: ScoreGap[] = []

  // Scored 0 below the minimum billing volume: reaching it comes first, worth what the
  // score would have been (review #695).
  if (sc.lowVolume) {
    const lv = sc.lowVolume
    const need = Math.max(1, Math.ceil(lv.bar - lv.claims))
    out.push({
      key: 'lowVolume',
      pts: Math.max(0, lv.computed),
      label: 'Reach the minimum billing volume',
      need: `Bill ${int(need)} more ${need === 1 ? 'claim' : 'claims'} to reach the minimum`,
      detail: `At ${int(lv.claims)} billed claims; the minimum is ${int(lv.bar)}, 90% of the bottom quarter's average (${int(lv.avg)}).`,
    })
  }

  if (sc.penalty.applied && sc.penalty.value !== null) {
    out.push({
      key: 'penalty',
      pts: -PENALTY.pts,
      label: 'Clear the under-50% penalty',
      need: `Close ${pts(PENALTY.threshold - sc.penalty.value)} more of what is billed`,
      detail: `At ${pctOf(sc.penalty.value)} billed vs resolved; the penalty lifts at 50%.`,
    })
  }

  for (const line of sc.lines) {
    if (line.earned > 0 || line.missing) continue
    if (line.value === null || line.peerAvg === null) continue
    out.push({
      key: line.key,
      pts: line.pts,
      label: line.label,
      need: GAP_TEXT[line.key](line.value, line.peerAvg),
      detail: `At ${fmtBy(line.key, line.value)} against a peer average of ${fmtBy(
        line.key,
        line.peerAvg,
      )}.`,
    })
  }

  // The minimum billing volume first, whatever it is worth: under it nothing else counts.
  return out.sort((a, b) => Number(b.key === 'lowVolume') - Number(a.key === 'lowVolume') || b.pts - a.pts)
}

/** Formats a value the way its own line is read. */
function fmtBy(key: ScoreKey, v: number): string {
  if (key === 'costPerClaim') return money2(v)
  if (key === 'claims' || key === 'facilities') return int(v)
  return pctOf(v)
}
